import React, { useEffect, useState } from "react";
import { Card, Empty, Popover, Statistic, Tag, Tooltip, Row, Col } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { DayStats, slotSeconds } from "./aggregations";
import { AutopodderResultRow, ScheduleDataItem } from "../types";
import { FixedOverfullProgressBar } from "../components/FixedOverfullProgressBar";
import { getColorForAdvertiser, getTextColorForBackground } from "../utils/colorUtils";

interface Props {
    day: DayStats | null;
    scheduleData: Record<string, ScheduleDataItem>;
}

const RAIL_WIDTH = 64;
const PX_PER_MIN = 12;
const BREAK_HEIGHT = 28;            // fixed-size break block, regardless of duration
const NON_MEDIA_MIN_HEIGHT = 22;
const COLLAPSED_HEIGHT = 22;
const TIMELINE_MAX_HEIGHT = "70vh";
const TIMELINE_PAD_PX = 7;          // breathing room so edge labels don't get clipped

const palette = {
    contentBg: "#ffffff",
    railBg: "#fafafa",
    border: "#e8e8e8",
    blockBorder: "#d9d9d9",
    hourText: "#595959",
    minuteText: "#bfbfbf",
    gridMin: "#f5f5f5",
    grid5: "#ebebeb",
    grid15: "#d9d9d9",
    gridHour: "#bfbfbf",
    fillGood: "#52c41a",
    fillOk: "#faad14",
    fillBad: "#ff4d4f",
    epBg: "#f0f5ff",
    epBgOpen: "#d6e4ff",
    epBorder: "#adc6ff",
    epBorderOpen: "#85a5ff",
    epAccent: "#2f54eb",
    epText: "#1d39c4",
};

const formatHourLabel = (h: number): string => {
    const ampm = h % 24 < 12 ? "AM" : "PM";
    const hh = h % 12 === 0 ? 12 : h % 12;
    return `${hh} ${ampm}`;
};

const fillColor = (pct: number) =>
    pct >= 95 ? palette.fillGood : pct >= 70 ? palette.fillOk : palette.fillBad;

const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s === 0 ? `${m}m` : `${m}m${s}s`;
};

const MAX_VISIBLE_PILLS = 5;

const gridBackground = [
    `repeating-linear-gradient(to bottom, transparent 0, transparent ${PX_PER_MIN - 1}px, ${palette.gridMin} ${PX_PER_MIN - 1}px, ${palette.gridMin} ${PX_PER_MIN}px)`,
    `repeating-linear-gradient(to bottom, transparent 0, transparent ${PX_PER_MIN * 5 - 1}px, ${palette.grid5} ${PX_PER_MIN * 5 - 1}px, ${palette.grid5} ${PX_PER_MIN * 5}px)`,
    `repeating-linear-gradient(to bottom, transparent 0, transparent ${PX_PER_MIN * 15 - 1}px, ${palette.grid15} ${PX_PER_MIN * 15 - 1}px, ${palette.grid15} ${PX_PER_MIN * 15}px)`,
    `repeating-linear-gradient(to bottom, transparent 0, transparent ${PX_PER_MIN * 60 - 1}px, ${palette.gridHour} ${PX_PER_MIN * 60 - 1}px, ${palette.gridHour} ${PX_PER_MIN * 60}px)`,
].join(", ");

// labelStartSec marks the earliest second from which this row may emit minute
// labels — clamped to the cumulative max endSec of prior rows so overlapping
// events don't double-emit the same minute boundary (e.g. 6 AM appearing in
// both an episode that ends at 6:01:33 and a break that starts at 6:00:00).
type TimelineRow =
    | { kind: "gap"; startSec: number; endSec: number; heightPx: number; labelStartSec: number }
    | { kind: "break"; startSec: number; endSec: number; heightPx: number; slot: AutopodderResultRow; labelStartSec: number }
    | { kind: "episode"; startSec: number; endSec: number; heightPx: number; slot: AutopodderResultRow; collapsed: boolean; labelStartSec: number };

const buildRows = (
    sortedSlots: AutopodderResultRow[],
    collapsed: Set<string>,
    rangeStartSec: number,
    rangeEndSec: number,
): TimelineRow[] => {
    const rows: TimelineRow[] = [];
    let prevEnd = rangeStartSec;
    let claimedUpToSec = rangeStartSec;

    const claim = (start: number, end: number): number => {
        const labelStart = Math.max(start, claimedUpToSec);
        claimedUpToSec = Math.max(claimedUpToSec, end);
        return labelStart;
    };

    for (const slot of sortedSlots) {
        const start = slotSeconds(slot);
        const dur = slot.SIZE_MAX || 0;
        const end = start + dur;

        if (start > prevEnd) {
            const gapStart = prevEnd;
            const gapEnd = start;
            rows.push({
                kind: "gap",
                startSec: gapStart,
                endSec: gapEnd,
                heightPx: ((gapEnd - gapStart) / 60) * PX_PER_MIN,
                labelStartSec: claim(gapStart, gapEnd),
            });
        }

        const isMedia = slot.TYPE === "media";
        if (isMedia) {
            // Fixed-height break block so all breaks visually match — duration
            // is shown as text inside. Time within the break is compressed,
            // similar to a collapsed episode.
            rows.push({
                kind: "break",
                startSec: start,
                endSec: end,
                heightPx: BREAK_HEIGHT,
                slot,
                labelStartSec: claim(start, end),
            });
        } else {
            const isCollapsed = collapsed.has(slot.ID);
            const propPx = (dur / 60) * PX_PER_MIN;
            rows.push({
                kind: "episode",
                startSec: start,
                endSec: end,
                heightPx: isCollapsed ? COLLAPSED_HEIGHT : Math.max(propPx, NON_MEDIA_MIN_HEIGHT),
                slot,
                collapsed: isCollapsed,
                labelStartSec: claim(start, end),
            });
        }

        prevEnd = end;
    }

    // Intentionally no trailing gap row — empty gridlines after the last
    // event are noise. The timeline ends with the last event.
    return rows;
};

const adPill = (
    adName: string,
    sched: ScheduleDataItem | undefined,
    keyPrefix: string,
) => {
    const advId = sched?.ADVERTISER_ID ?? "";
    const bg = advId ? getColorForAdvertiser(advId) : "#f0f0f0";
    const fg = getTextColorForBackground(bg);
    // UNITLENGTH is 0 in some batches (e.g. broadcast filler/promo inventory);
    // LENGTH is the more reliable length field on those rows.
    const adLength = sched ? (sched.UNITLENGTH || sched.LENGTH || 0) : 0;
    const tooltip = sched ? (
        <div style={{ fontSize: 12 }}>
            <div><b>{adName}</b></div>
            <div>{sched.DEALADVERTISER}</div>
            <div>{sched.DEALBRAND}</div>
            <div>{adLength}s · ${sched.RATE_STR}</div>
            <div>Max/day: {sched.MAX_PER_DAY}</div>
        </div>
    ) : adName;
    return (
        <Tooltip key={keyPrefix} title={tooltip}>
            <span
                style={{
                    background: bg,
                    color: fg,
                    padding: "1px 6px",
                    borderRadius: 3,
                    fontSize: 11,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    lineHeight: "16px",
                }}
            >
                {adName}
                {adLength ? <span style={{ opacity: 0.7, marginLeft: 3 }}>:{adLength}</span> : null}
            </span>
        </Tooltip>
    );
};

const MediaBlock: React.FC<{
    slot: AutopodderResultRow;
    breakNum: number;
    scheduleData: Record<string, ScheduleDataItem>;
}> = ({ slot, breakNum, scheduleData }) => {
    const fillPct = slot.SIZE_MAX > 0
        ? Math.round((slot.SELECTED_AD_COMBO_TOTAL_ADS_LENGTH / slot.SIZE_MAX) * 100)
        : 0;
    const adNames = slot.SELECTED_AD_COMBO_ADS_STR
        ? slot.SELECTED_AD_COMBO_ADS_STR.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    const accent = fillColor(fillPct);

    const popoverContent = (
        <div style={{ fontSize: 12, minWidth: 220 }}>
            <div style={{ marginBottom: 6 }}>
                <b>Break #{breakNum}</b>
                {slot.SEGMENT_ID && (
                    <span style={{ marginLeft: 8, color: "#8c8c8c" }}>seg {slot.SEGMENT_ID}</span>
                )}
            </div>
            <div style={{ color: "#595959" }}>{slot.SLOT_ID}</div>
            <div style={{ color: "#595959" }}>Fill order #{slot.FILLORDER}</div>
            <div style={{ color: "#595959", marginBottom: 6 }}>
                {slot.SELECTED_AD_COMBO_TOTAL_ADS_LENGTH}s / {slot.SIZE_MAX}s
            </div>
            <FixedOverfullProgressBar
                variant="line"
                currentAmount={slot.SELECTED_AD_COMBO_TOTAL_ADS_LENGTH}
                totalAmount={slot.SIZE_MAX}
            />
            {adNames.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <div style={{ marginBottom: 4, fontWeight: 600, color: "#595959" }}>
                        Ads ({adNames.length}):
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {adNames.map((name, idx) =>
                            adPill(name, scheduleData[name], `popover-${slot.ID}-${name}-${idx}`),
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    const visiblePills = adNames.slice(0, MAX_VISIBLE_PILLS);
    const hiddenCount = Math.max(0, adNames.length - MAX_VISIBLE_PILLS);

    return (
        <Popover content={popoverContent} placement="left" mouseEnterDelay={0.3}>
            <div
                style={{
                    padding: "3px 6px 3px 9px",
                    border: `1px solid ${palette.blockBorder}`,
                    borderLeft: `3px solid ${accent}`,
                    borderRadius: 3,
                    background: palette.contentBg,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                    height: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                }}
            >
                <span style={{ fontSize: 11, fontWeight: 600, color: "#8c8c8c" }}>
                    {formatDuration(slot.SIZE_MAX)}
                </span>
                {fillPct < 100 && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: accent }}>
                        {fillPct}%
                    </span>
                )}
                <span style={{ fontSize: 11, fontWeight: 600, color: slot.SELECTED_AD_COMBO_TOTAL_DOLLARS > 0 ? "#389e0d" : "#bfbfbf" }}>
                    ${slot.SELECTED_AD_COMBO_TOTAL_DOLLARS.toLocaleString()}
                </span>
                {adNames.length === 0 ? (
                    <span style={{ fontSize: 11, color: "#bfbfbf", fontStyle: "italic" }}>empty</span>
                ) : (
                    <>
                        {visiblePills.map((adName, idx) =>
                            adPill(adName, scheduleData[adName], `${slot.ID}-${adName}-${idx}`),
                        )}
                        {hiddenCount > 0 && (
                            <span
                                style={{
                                    fontSize: 11,
                                    color: "#595959",
                                    background: "#f0f0f0",
                                    border: "1px solid #d9d9d9",
                                    padding: "1px 6px",
                                    borderRadius: 3,
                                    whiteSpace: "nowrap",
                                    lineHeight: "16px",
                                    fontWeight: 500,
                                }}
                            >
                                +{hiddenCount}
                            </span>
                        )}
                    </>
                )}
            </div>
        </Popover>
    );
};

const EpisodeBlock: React.FC<{
    slot: AutopodderResultRow;
    collapsed: boolean;
    onToggle: () => void;
}> = ({ slot, collapsed, onToggle }) => {
    const label = slot.TITLE
        ? slot.TITLE
        : slot.TYPE === "episode"
            ? "Episode"
            : slot.TYPE;
    return (
        <div
            onClick={onToggle}
            style={{
                cursor: "pointer",
                padding: "2px 6px 2px 8px",
                background: collapsed ? palette.epBg : palette.epBgOpen,
                border: `1px solid ${collapsed ? palette.epBorder : palette.epBorderOpen}`,
                borderLeft: `3px solid ${palette.epAccent}`,
                borderRadius: 3,
                boxShadow: "0 1px 2px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: palette.epText,
                height: "100%",
                boxSizing: "border-box",
                userSelect: "none",
                overflow: "hidden",
            }}
        >
            <span style={{ flexShrink: 0, fontWeight: 600 }}>
                {formatDuration(slot.SIZE_MAX || 0)}
            </span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
            </span>
            <span style={{ flexShrink: 0, fontSize: 10, opacity: 0.7 }}>{collapsed ? "▸" : "▾"}</span>
        </div>
    );
};

const RowView: React.FC<{
    row: TimelineRow;
    breakIndexById: Map<string, number>;
    scheduleData: Record<string, ScheduleDataItem>;
    onToggle: (id: string) => void;
}> = ({ row, breakIndexById, scheduleData, onToggle }) => {
    // Two label modes:
    //   - "proportional": gaps and expanded episodes — every minute, scaled by PX_PER_MIN
    //   - "compressed":   breaks and collapsed episodes — only hour labels, proportional within
    //                     the row's compressed pixel height
    const proportional = row.kind === "gap" || (row.kind === "episode" && !row.collapsed);
    const compressed = row.kind === "break" || (row.kind === "episode" && row.collapsed);
    const showGrid = proportional;

    const rawLabels: Array<{ minute: number; y: number; major: boolean }> = [];
    if (proportional) {
        const startMinute = Math.ceil(row.labelStartSec / 60);
        for (let m = startMinute; m * 60 < row.endSec; m++) {
            const y = ((m * 60 - row.startSec) / 60) * PX_PER_MIN;
            if (y > row.heightPx) break;
            rawLabels.push({ minute: m, y, major: m % 60 === 0 });
        }
    } else if (compressed) {
        // Break rows show all minute labels squeezed into the fixed rail height.
        // Collapsed episode rows show only hour labels — their interior time is
        // intentionally hidden.
        const onlyHours = row.kind === "episode" && row.collapsed;
        const startMinute = Math.ceil(row.labelStartSec / 60);
        const span = Math.max(1, row.endSec - row.startSec);
        for (let m = startMinute; m * 60 < row.endSec; m++) {
            const isHour = m % 60 === 0;
            if (onlyHours && !isHour) continue;
            const y = ((m * 60 - row.startSec) / span) * row.heightPx;
            rawLabels.push({ minute: m, y, major: isHour });
        }
    }

    // Event-start marker: every break/episode row gets a start-time tag at
    // its top, plus a horizontal accent line spanning the row width. The
    // accent color matches the block's left border so the marker reads as
    // "this is where this event begins". Suppress regular MINOR minute
    // labels within START_LABEL_BAND_PX of the row top so they don't
    // collide. Major (hour) labels are kept regardless since they carry
    // more info; if one falls in the start band we suppress the start-time
    // label instead, deferring to the hour label.
    const isEventStart = row.kind === "break" || row.kind === "episode";
    const START_LABEL_BAND_PX = 14;
    const ACCENT_LINE_PX = 2;
    const MIN_HOUR_LABEL_Y = ACCENT_LINE_PX + PX_PER_MIN / 2; // clear of accent line
    const hourInStartBand = isEventStart && rawLabels.some(
        (l) => l.major && l.y < START_LABEL_BAND_PX,
    );
    const showStartTimeLabel = isEventStart && !hourInStartBand;
    const labels = isEventStart
        ? rawLabels
            .filter((l) => l.major || l.y >= START_LABEL_BAND_PX)
            .map((l) =>
                // Hour labels positioned under the 2px accent line get bisected
                // by it. Push them down just enough to clear the line.
                l.major && l.y < MIN_HOUR_LABEL_Y ? { ...l, y: MIN_HOUR_LABEL_Y } : l,
            )
        : rawLabels;

    let startAccent = "transparent";
    if (row.kind === "break") {
        const fillPct = row.slot.SIZE_MAX > 0
            ? Math.round((row.slot.SELECTED_AD_COMBO_TOTAL_ADS_LENGTH / row.slot.SIZE_MAX) * 100)
            : 0;
        startAccent = fillColor(fillPct);
    } else if (row.kind === "episode") {
        startAccent = palette.epAccent;
    }
    const startMinPastHour = Math.floor(row.startSec / 60) % 60;
    const startSecond = Math.floor(row.startSec % 60);
    const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const startTimeLabel = `:${pad2(startMinPastHour)}:${pad2(startSecond)}`;

    // Background gradient offset so minute lines land at true minute boundaries
    // even when the row starts at a non-integer minute (e.g. 12:00:33).
    const gridOffsetY = -((row.startSec % 60) / 60) * PX_PER_MIN;

    return (
        <div style={{ display: "flex", flexShrink: 0, height: row.heightPx, position: "relative" }}>
            {isEventStart && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 4,
                        height: 2,
                        background: startAccent,
                        zIndex: 3,
                        pointerEvents: "none",
                    }}
                />
            )}
            <div
                style={{
                    width: RAIL_WIDTH,
                    flexShrink: 0,
                    position: "relative",
                    background: palette.railBg,
                    borderRight: `1px solid ${palette.border}`,
                }}
            >
                {labels.map(({ minute, y, major }) => (
                    <div
                        key={minute}
                        style={{
                            position: "absolute",
                            top: y - PX_PER_MIN / 2,
                            left: 0,
                            right: 0,
                            height: PX_PER_MIN,
                            lineHeight: `${PX_PER_MIN}px`,
                            fontSize: major ? 11 : 9,
                            fontWeight: major ? 600 : 400,
                            color: major ? palette.hourText : palette.minuteText,
                            textAlign: "right",
                            paddingRight: 6,
                            pointerEvents: "none",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {major
                            ? formatHourLabel(Math.floor(minute / 60))
                            : `:${pad2(minute % 60)}:00`}
                    </div>
                ))}
                {showStartTimeLabel && (
                    <div
                        style={{
                            position: "absolute",
                            top: 2,
                            left: 0,
                            right: 0,
                            height: PX_PER_MIN,
                            lineHeight: `${PX_PER_MIN}px`,
                            fontSize: 9,
                            fontWeight: 400,
                            color: startAccent,
                            textAlign: "right",
                            paddingRight: 6,
                            pointerEvents: "none",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            zIndex: 4,
                        }}
                    >
                        {startTimeLabel}
                    </div>
                )}
            </div>
            <div
                style={{
                    flex: 1,
                    position: "relative",
                    backgroundImage: showGrid ? gridBackground : undefined,
                    backgroundPositionY: showGrid ? `${gridOffsetY}px` : undefined,
                }}
            >
                {row.kind === "break" && (
                    <div style={{ position: "absolute", top: 1, bottom: 1, left: 4, right: 4 }}>
                        <MediaBlock
                            slot={row.slot}
                            breakNum={breakIndexById.get(row.slot.ID) ?? 0}
                            scheduleData={scheduleData}
                        />
                    </div>
                )}
                {row.kind === "episode" && (
                    <div style={{ position: "absolute", top: 1, bottom: 1, left: 4, right: 4 }}>
                        <EpisodeBlock
                            slot={row.slot}
                            collapsed={row.collapsed}
                            onToggle={() => onToggle(row.slot.ID)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const DayDetailPanel: React.FC<Props> = ({ day, scheduleData }) => {
    const dayKey = day?.isoKey ?? null;
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    // Default-collapse every episode when the selected day changes.
    useEffect(() => {
        if (!day) {
            setCollapsed(new Set());
            return;
        }
        const next = new Set<string>();
        for (const s of day.slots) {
            if (s.TYPE !== "media") next.add(s.ID);
        }
        setCollapsed(next);
    }, [dayKey, day]);

    if (!day) {
        return (
            <Card style={{ minHeight: 400 }} bodyStyle={{ background: "#f0f0f0", borderRadius: 8 }}>
                <Empty description="Select a day to see slot details" />
            </Card>
        );
    }

    const breakIndexById = new Map<string, number>();
    day.mediaSlots.forEach((s, i) => breakIndexById.set(s.ID, i + 1));

    const sortedSlots = [...day.slots].sort((a, b) => slotSeconds(a) - slotSeconds(b));
    const slotStarts = sortedSlots.map(slotSeconds);
    const slotEnds = sortedSlots.map((s) => slotSeconds(s) + (s.SIZE_MAX || 0));
    const minSec = slotStarts.length > 0 ? Math.min(...slotStarts) : 0;
    const maxSec = slotEnds.length > 0 ? Math.max(...slotEnds) : 3600;
    const rangeStartSec = Math.floor(minSec / 3600) * 3600;
    const rangeEndSec = Math.ceil(maxSec / 3600) * 3600;

    const rows = buildRows(sortedSlots, collapsed, rangeStartSec, rangeEndSec);

    const toggle = (id: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span>{day.dayOfWeek}, {day.dateStr}</span>
                    {day.maxPerDayViolations.length > 0 && (
                        <Tag color="error" icon={<WarningFilled />}>
                            {day.maxPerDayViolations.length} max-per-day violation
                            {day.maxPerDayViolations.length === 1 ? "" : "s"}
                        </Tag>
                    )}
                </div>
            }
            bodyStyle={{ background: "#f0f0f0", borderRadius: 8 }}
        >
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Statistic title="Breaks" value={day.slotCount} />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Fill"
                        value={day.fillPct}
                        suffix="%"
                        precision={1}
                        valueStyle={{ color: fillColor(day.fillPct) }}
                    />
                </Col>
                <Col span={6}>
                    <Statistic title="Unfilled" value={day.totalUnfilled} suffix="s" />
                </Col>
                <Col span={6}>
                    <Statistic title="Total $" value={day.totalDollars} prefix="$" />
                </Col>
            </Row>

            {day.maxPerDayViolations.length > 0 && (
                <div style={{ marginBottom: 16, padding: 8, background: "#fff1f0", border: "1px solid #ffccc7", borderRadius: 4 }}>
                    <div style={{ fontSize: 12, color: "#cf1322", marginBottom: 4, fontWeight: 600 }}>
                        Max per day exceeded:
                    </div>
                    {day.maxPerDayViolations.map((v) => (
                        <div key={v.adName} style={{ fontSize: 12, color: "#cf1322" }}>
                            {v.adName}: {v.used} airings (cap {v.cap})
                        </div>
                    ))}
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    background: palette.contentBg,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 4,
                    overflow: "auto",
                    maxHeight: TIMELINE_MAX_HEIGHT,
                    paddingTop: TIMELINE_PAD_PX,
                    paddingBottom: TIMELINE_PAD_PX,
                }}
            >
                {rows.map((row, i) => (
                    <RowView
                        key={`${row.kind}-${row.startSec}-${i}`}
                        row={row}
                        breakIndexById={breakIndexById}
                        scheduleData={scheduleData}
                        onToggle={toggle}
                    />
                ))}
            </div>
        </Card>
    );
};

export default DayDetailPanel;
